import type { Block } from "@/types/block";
import { serializeDateCell } from "@/lib/date-value";
import { getAmbientTimeZone, zonedNow } from "@/lib/timezone";

// System columns show the row's own metadata (who made it, when it last
// changed) instead of a stored cell. They are read-only everywhere.

export const SYSTEM_TIME_TYPES = ["created_time", "last_edited_time"] as const;
export const SYSTEM_PERSON_TYPES = ["created_by", "last_edited_by"] as const;
export const SYSTEM_PROPERTY_TYPES = [...SYSTEM_TIME_TYPES, ...SYSTEM_PERSON_TYPES] as const;

export type SystemTimeType = (typeof SYSTEM_TIME_TYPES)[number];
export type SystemPersonType = (typeof SYSTEM_PERSON_TYPES)[number];
export type SystemPropertyType = SystemTimeType | SystemPersonType;

export function isSystemTimeType(type: unknown): type is SystemTimeType {
  return SYSTEM_TIME_TYPES.includes(type as SystemTimeType);
}

export function isSystemPersonType(type: unknown): type is SystemPersonType {
  return SYSTEM_PERSON_TYPES.includes(type as SystemPersonType);
}

export function isSystemPropertyType(type: unknown): type is SystemPropertyType {
  return isSystemTimeType(type) || isSystemPersonType(type);
}

/** The stored type a system column borrows its rendering, filters and sorts from. */
export function presentationType(type: string): string {
  if (isSystemTimeType(type)) return "date";
  if (isSystemPersonType(type)) return "person";
  return type;
}

/** The type a column behaves like: a formula borrows its return type, a boolean one the checkbox UI. */
export function effectiveType(prop?: { type?: string; formulaReturnType?: string } | null): string {
  if (!prop?.type) return "";
  if (prop.type === "formula") {
    const ret = prop.formulaReturnType ?? "text";
    return ret === "boolean" ? "checkbox" : ret;
  }
  return presentationType(prop.type);
}

export type RowMeta = Pick<Block, "createdAt" | "createdBy" | "updatedAt" | "lastEditedBy">;
type RowLike = RowMeta & Partial<Pick<Block, "value">>;

/**
 * Server instants become the timed local string date cells store, so date code
 * reads them unchanged.
 *
 * created_time / last_edited_time are true instants, so they are flattened in
 * the VIEWER's zone — not the device's. Read from the ambient slot because
 * getCellValue has two dozen call sites across filtering, sorting and display,
 * and threading a zone through all of them to reach one conversion is not worth
 * it. On the server the ambient is undefined, which is the old behaviour.
 */
function toLocalDateCell(iso: string | Date | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return serializeDateCell(zonedNow(getAmbientTimeZone(), date), null, true);
}

/** A system column's value, shaped like the cell type it borrows from. Empty when the row has no stamp yet. */
export function readSystemPropertyValue(row: RowMeta, type: SystemPropertyType) {
  switch (type) {
    case "created_time":
      return toLocalDateCell(row.createdAt);
    case "last_edited_time":
      return toLocalDateCell(row.updatedAt);
    case "created_by":
      return row.createdBy ? [row.createdBy] : [];
    case "last_edited_by":
      return row.lastEditedBy ? [row.lastEditedBy] : [];
  }
}

/** Exact instant behind a time column; sorting uses this rather than the minute-precision cell string. */
export function systemTimeInstant(row: RowMeta, type: SystemTimeType): number | null {
  const iso = type === "created_time" ? row.createdAt : row.updatedAt;
  const t = iso ? new Date(iso).getTime() : Number.NaN;
  return Number.isNaN(t) ? null : t;
}

/** Cell value for any column: system columns derive from the row, the rest read the cell map. */
export function getCellValue(row: RowLike, propertyId: string, property?: { type?: string } | null) {
  const type = property?.type;
  if (isSystemPropertyType(type)) return readSystemPropertyValue(row, type);
  return row.value?.databaseProperties?.[propertyId];
}

/** The page cache's edit stamp, as the page header label reads it. */
export interface PageEditStamp {
  lastEditedAt?: number;
  lastEditedBy?: RowMeta["lastEditedBy"];
  lastEditedByAt?: number;
}

/**
 * The row with the page cache's stamp overlaid when it is newer than the
 * block's — the header label's "newest wins" rule. A cache time past the last
 * confirmed author means the newest edit was made here, so it is credited to
 * `self`.
 */
export function overlayEditStamp<T extends RowMeta>(
  row: T,
  entry: PageEditStamp | undefined,
  self: RowMeta["lastEditedBy"] | null,
): T {
  const cachedAt = entry?.lastEditedAt;
  if (cachedAt == null) return row;
  const blockAt = row.updatedAt ? new Date(row.updatedAt).getTime() : Number.NaN;
  if (!Number.isNaN(blockAt) && blockAt >= cachedAt) return row;
  const confirmedAt = entry?.lastEditedByAt;
  const editedHere = confirmedAt != null && cachedAt > confirmedAt;
  const editor = (editedHere ? self : null) ?? entry?.lastEditedBy ?? row.lastEditedBy;
  return { ...row, updatedAt: new Date(cachedAt).toISOString(), lastEditedBy: editor };
}
